import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import { FormProvider, useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { GraphQLFormattedError } from 'graphql';
import { Values } from '@lib/interfaces/form';
import PageSteps from '@lib/layouts/DashboardLayout/PageSteps/PageSteps';
import { Steps } from '@lib/enums/form';
import useStepParam from '@lib/hooks/useStepParam';
import Portal from '@lib/components/Portal/Portal';
import usePrevious from '@lib/hooks/usePrevious';
import replaceEmptyStringWithNull from '@lib/utils/replaceEmptyStringWithNull';
import { pageStepsId } from '@lib/enums/selectors';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import isDeeplyEmpty from '@lib/utils/isDeeplyEmpty';
import useFormPersistValues from '@lib/components/ReactHookForm/useFormPersistValues';
import isEqualWithSkipKeys from '@lib/utils/isEqualWithSkipKeys';
import { IconName } from '@lib/components/Modal/enums';
import {
  createThrottledPersist,
  DEFAULT_PERSIST_THROTTLE_MS,
} from './utils/throttlePersist';
import { FormContainerProps, FormRef } from './types';
import getFormData from './getFormData';
import getErrorsByStep from './getErrorsByStep';
import useHandleGraphQLErrors from './useHandleGraphQLErrors';
import mergeDefaultValuesPreservingDirtyFields from './mergeDefaultValuesPreservingDirtyFields';
import FormProviderCustom from './FormProvider';
import Form from './Form';
import logSubmitError from './utils/logSubmitError';

export type { FormRef, ActionsProps } from './types';

const DIRTY_SKIP_KEYS = ['avatarAttached'];
// Safety net for callback chains the name-based cycle check can't see
// (e.g. events without a `name`); real cascades are a handful of levels deep.
const MAX_CALLBACK_CHAIN_DEPTH = 20;

/**
 * Entrypoint component that wires together React Hook Form with extra behaviours such as
 * optimistic persistence, navigation blocking and custom dirty tracking.
 */
function FormContainer(props: FormContainerProps) {
  const { t } = useTranslation();
  const {
    actions,
    actionsComponent,
    actionsComponentProps,
    autoComplete,
    defaultValues,
    disableAwayConfirmation = false,
    fields,
    formClassName,
    formId = 'form',
    formItemsClassName,
    formRef,
    graphQLErrors,
    graphQLErrorsReplacePathMap,
    graphQLErrorsSkipPath,
    loading = false,
    loadingDesc = t('processing-is-ongoing'),
    onSubmit,
    persistForm = false,
    persistFormSeconds = 0,
    persistExcludeFields,
    preserveDirtyOnDefaultValuesChange = true,
    renderAfterForm,
    sourceAppendValues,
    sourceFields,
    steps,
    stepsCount,
    submitOnChange = false,
  } = props;
  // Only an explicit formId becomes the <form> element id: the 'form'
  // fallback would repeat across every form on the page.
  const { formId: formIdProp } = props;
  const prevDefaultValues = usePrevious(defaultValues);
  const [stepParam] = useStepParam();
  const [isAwayModalOpen, setAwayModalOpen] = useState(false);

  const stepIndex =
    steps && steps.length > 1 && stepParam ? Number(stepParam) : Steps.step1;

  const { setPersistedValues, getPersistedValues } = useFormPersistValues({
    formId,
    persistForm,
    persistFormSeconds,
  });

  const throttledSetPersistedValues = useMemo(
    () =>
      createThrottledPersist(setPersistedValues, DEFAULT_PERSIST_THROTTLE_MS),
    [setPersistedValues],
  );

  useEffect(
    () => () => throttledSetPersistedValues.flush(),
    [throttledSetPersistedValues],
  );

  const persistedValues = getPersistedValues();

  const { fieldsArray, schemaObj, fieldChangeCallBacks } = useMemo(
    () =>
      getFormData({
        fields,
        formId,
        formItemsClassName,
        stepIndex,
        t,
        sourceAppendValues,
      }),
    [fields, formId, formItemsClassName, sourceAppendValues, stepIndex, t],
  );
  const formDefaultValues = useMemo(
    () => ({
      ...defaultValues,
      ...(persistExcludeFields?.length
        ? _omit(persistedValues, persistExcludeFields)
        : persistedValues),
    }),
    [defaultValues, persistedValues, persistExcludeFields],
  );
  // Rebuilt only when the field config changes, not on every render.
  const resolver = useMemo(
    () =>
      // Collect all validation errors, not just the first
      // Note: yupResolver already defaults to abortEarly: false; passing as 2nd arg to satisfy types
      yupResolver(Yup.object(schemaObj), { abortEarly: false }),
    [schemaObj],
  );
  const methods = useForm({
    // @ts-ignore
    defaultValues: formDefaultValues,
    // `all` validates on every change and blur, before and after submit, so
    // RHF never consults `reValidateMode`.
    mode: 'all',
    // @ts-ignore - schema is built dynamically from schemaObj, so @hookform/resolvers v5 infers
    // an empty ({ [x: string]: never }) Resolver shape that doesn't match the dynamic form Values
    resolver,
    shouldFocusError: true,
  });
  const {
    handleSubmit,
    formState,
    reset: rhfReset,
    setError,
    setValue,
    watch,
    getValues,
    trigger,
    control,
  } = methods;
  const skipCallbacksRef = useRef(false);
  // Field names whose change callbacks are currently running (outermost
  // first); guards the watch handler against callback cycles.
  const callbackChainRef = useRef<string[]>([]);
  /**
   * Creates a deep copy of the current form values to decouple baseline snapshots
   * from RHF's internal mutable state.
   * Values can hold functions (e.g. a question's `renderCustomIcon`), Files
   * and Dates; cloneDeep keeps them intact so the copy still equals the live
   * values and a successful submit leaves the form clean.
   */
  const cloneValues = useCallback(
    (source?: Values): Values => _cloneDeep(source ?? getValues()),
    [getValues],
  );
  /**
   * Wrapper around RHF setValue that keeps dirty tracking consistent during
   * hydration and user-driven updates.
   */
  const setValueWithDirty = useCallback<typeof setValue>(
    (name, value, options) => {
      const shouldDirty = options?.shouldDirty ?? !skipCallbacksRef.current;
      return setValue(name, value, {
        ...options,
        shouldDirty,
      });
    },
    [setValue],
  );
  const unmuteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baselineValuesRef = useRef<Values>(cloneValues());
  const hasLocalChangesRef = useRef(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  // The submit in progress or last settled. Lets a server failure reported
  // through `graphQLErrors` (Apollo `onError` resolves instead of rejecting)
  // keep or restore the dirty state, whichever commit the errors land in.
  const submitAttemptRef = useRef<{
    baseline: Values;
    errorsAtStart?: readonly GraphQLFormattedError[];
    failed: boolean;
    settled: boolean;
    // Form values as they were when the submit started.
    submitted: Values;
  } | null>(null);
  const graphQLErrorsRef = useRef(graphQLErrors);
  graphQLErrorsRef.current = graphQLErrors;
  // Errors count for an attempt only if they are non-empty and differ from
  // what the prop held when the submit started (not a stale earlier failure).
  const isNewSubmitError = (errors?: readonly GraphQLFormattedError[]) => {
    const attempt = submitAttemptRef.current;
    return !!attempt && !!errors?.length && errors !== attempt.errorsAtStart;
  };
  const lastHandledErrorsRef = useRef(graphQLErrors);

  /**
   * Determines whether new form values diverge from the baseline snapshot.
   */
  const evaluateChanges = useCallback(
    (nextValues: Values, fieldName?: string) => {
      const baseline = baselineValuesRef.current;
      if (!fieldName) {
        return !isEqualWithSkipKeys(nextValues, baseline, DIRTY_SKIP_KEYS);
      }
      const baselineValue = _get(baseline, fieldName);
      const currentValue = _get(nextValues, fieldName);
      if (!_isEqual(baselineValue, currentValue)) {
        return true;
      }
      if (hasLocalChangesRef.current) {
        return !isEqualWithSkipKeys(nextValues, baseline, DIRTY_SKIP_KEYS);
      }
      return false;
    },
    [],
  );

  const refreshBaseline = useCallback(() => {
    baselineValuesRef.current = cloneValues();
    hasLocalChangesRef.current = false;
    setHasLocalChanges(false);
  }, [cloneValues]);
  const clearPendingUnmute = useCallback(() => {
    if (unmuteTimeoutRef.current) {
      clearTimeout(unmuteTimeoutRef.current);
      unmuteTimeoutRef.current = null;
    }
  }, []);
  /**
   * Re-enables change callbacks on the next tick and captures a fresh baseline.
   */
  const scheduleUnmute = useCallback(() => {
    clearPendingUnmute();
    unmuteTimeoutRef.current = setTimeout(() => {
      skipCallbacksRef.current = false;
      unmuteTimeoutRef.current = null;
      refreshBaseline();
    }, 0);
  }, [clearPendingUnmute, refreshBaseline]);
  const resetForm = useCallback(
    (
      resetValues?: Parameters<typeof rhfReset>[0],
      resetOptions?: Parameters<typeof rhfReset>[1],
    ) => {
      skipCallbacksRef.current = true;
      rhfReset(resetValues, resetOptions);
      scheduleUnmute();
    },
    [rhfReset, scheduleUnmute],
  );
  const { errors, isSubmitSuccessful, dirtyFields, submitCount } = formState;

  const isStepsVisible = !!stepsCount && !!steps?.length;

  const {
    proceed = _noop,
    state,
    reset: blockerReset,
  } = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !disableAwayConfirmation &&
      hasLocalChanges &&
      !isDeeplyEmpty(getValues()) &&
      !_isEqual(currentLocation.pathname, nextLocation.pathname),
  );

  // Returns onSubmit's result so react-hook-form awaits it: a rejected
  // promise keeps isSubmitSuccessful false and the form stays dirty.
  const onSubmitHandler = useCallback(
    async (newValues: Values) => {
      submitAttemptRef.current = {
        baseline: baselineValuesRef.current,
        errorsAtStart: graphQLErrorsRef.current,
        failed: false,
        settled: false,
        submitted: cloneValues(),
      };
      if (onSubmit)
        await onSubmit(
          replaceEmptyStringWithNull(newValues, ['locationGeojson']),
        );
    },
    [cloneValues, onSubmit],
  );

  const onAwayConfirmed = () => {
    setAwayModalOpen(false);
    resetForm();
    proceed();
  };

  const onCloseAwayModal = () => {
    setAwayModalOpen(false);
    if (blockerReset) blockerReset();
  };

  // Getters read live state, so callers outside the render cycle (event
  // handlers, modal callbacks) never see a stale snapshot.
  const formRefValue = useMemo<FormRef>(
    () => ({
      reset: resetForm,
      setValue: setValueWithDirty,
      get errors() {
        // control._formState is RHF's live state (formState is a render snapshot)
        // eslint-disable-next-line no-underscore-dangle
        return control._formState.errors;
      },
      get values() {
        return getValues();
      },
      get isDirty() {
        return hasLocalChangesRef.current;
      },
    }),
    [control, getValues, resetForm, setValueWithDirty],
  );
  if (formRef) {
    formRef.current = formRefValue;
  }

  useEffect(() => {
    if (
      defaultValues &&
      prevDefaultValues &&
      !isEqualWithSkipKeys(defaultValues, prevDefaultValues, ['avatarAttached'])
    ) {
      // A field the user already edited (e.g. while an async job was still
      // populating it) must keep the user's value — otherwise the refreshed
      // defaultValues silently overwrites it once the job's data lands.
      // Some forms (e.g. a filter form) want the opposite: an external
      // defaultValues change, such as a reset, should always win outright.
      resetForm(
        preserveDirtyOnDefaultValuesChange
          ? mergeDefaultValuesPreservingDirtyFields(
              getValues(),
              defaultValues,
              dirtyFields,
            )
          : { ...defaultValues },
      );
      refreshBaseline();
      submitAttemptRef.current = null;
    }
  }, [
    defaultValues,
    getValues,
    prevDefaultValues,
    dirtyFields,
    preserveDirtyOnDefaultValuesChange,
    refreshBaseline,
    resetForm,
  ]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      // Read the form's CURRENT values here instead of closing over `values`
      // from this render: the defaultValues-refresh effect above may have
      // already reset the form (with the refreshed defaultValues merged in)
      // in this SAME commit, since effects run in declaration order. Reusing
      // the render-time `values` snapshot would still be the pre-refresh
      // state and would silently clobber that merge.
      const attempt = submitAttemptRef.current;
      if (attempt?.failed || isNewSubmitError(graphQLErrorsRef.current)) {
        // The server already reported this submit as failed (errors landed
        // in an earlier or the same commit): keep the unsaved changes.
        submitAttemptRef.current = null;
        return;
      }
      if (
        attempt &&
        !isEqualWithSkipKeys(getValues(), attempt.submitted, DIRTY_SKIP_KEYS)
      ) {
        // The user edited the form while the submit was in flight: only the
        // submitted values were saved, so keep the newer edits dirty.
        baselineValuesRef.current = attempt.submitted;
        attempt.settled = true;
        const nextHasChanges = evaluateChanges(getValues());
        hasLocalChangesRef.current = nextHasChanges;
        setHasLocalChanges(nextHasChanges);
        return;
      }
      resetForm(cloneValues(getValues()), {
        keepDirty: false,
        keepDirtyValues: false,
      });
      refreshBaseline();
      if (attempt) attempt.settled = true;
    }
    // submitCount re-runs this per submit: when the reset is skipped above,
    // RHF's isSubmitSuccessful stays true into the next submit.
  }, [
    cloneValues,
    evaluateChanges,
    getValues,
    isSubmitSuccessful,
    refreshBaseline,
    resetForm,
    submitCount,
  ]);

  // onSubmit callers that report failures through `graphQLErrors` (Apollo
  // `onError`) resolve instead of rejecting, so the submit above already
  // counted as successful. Put the pre-submit baseline back so the unsaved
  // changes are tracked (Save enabled, leave-page blocker on) again.
  useEffect(() => {
    // React only to a new errors value, never to dependency identity changes.
    if (graphQLErrors === lastHandledErrorsRef.current) return;
    lastHandledErrorsRef.current = graphQLErrors;
    const attempt = submitAttemptRef.current;
    if (!attempt || !isNewSubmitError(graphQLErrors)) return;
    if (!attempt.settled) {
      // Submit still in flight: the success effect will skip its reset.
      attempt.failed = true;
      return;
    }
    submitAttemptRef.current = null;
    baselineValuesRef.current = attempt.baseline;
    const nextHasChanges = evaluateChanges(getValues());
    hasLocalChangesRef.current = nextHasChanges;
    setHasLocalChanges(nextHasChanges);
  }, [evaluateChanges, getValues, graphQLErrors]);

  useEffect(() => {
    const subscription = watch((newValues, { name, type }) => {
      if (submitOnChange && type === 'change') {
        handleSubmit(onSubmitHandler)().catch(logSubmitError);
      }
      // A user edit after a settled submit starts a new baseline: later
      // errors no longer belong to that submit.
      if (type === 'change' && submitAttemptRef.current?.settled) {
        submitAttemptRef.current = null;
      }
      // @ts-ignore
      throttledSetPersistedValues(newValues);
      if (skipCallbacksRef.current) {
        return;
      }
      const nextHasChanges = evaluateChanges(
        newValues as Values,
        name as string | undefined,
      );
      if (nextHasChanges !== hasLocalChangesRef.current) {
        hasLocalChangesRef.current = nextHasChanges;
        setHasLocalChanges(nextHasChanges);
      }
      // A callback's own setValue() re-enters this handler synchronously (RHF
      // emits a watch event with `type: undefined`, even for an unchanged
      // value on a controlled field). Cascades (Site -> Area -> Asset) are
      // fine, but a field re-appearing in the chain that is already
      // dispatching it is a cycle (Email <-> Username, STAGING-ADMIN-84) and
      // would recurse until the stack overflows — so stop it there.
      const chain = callbackChainRef.current;
      if (
        (name && chain.includes(name)) ||
        chain.length >= MAX_CALLBACK_CHAIN_DEPTH
      ) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[FormContainer] fieldChangeCallBack chain cut: ${[...chain, name ?? '?'].join(' → ')}`,
          );
        }
        return;
      }
      chain.push(name ?? '');
      try {
        // Use Set to avoid duplicate callback executions
        const uniqueCallbacks = [...new Set(fieldChangeCallBacks)];
        uniqueCallbacks.forEach((fieldChangeCallBack) => {
          if (fieldChangeCallBack) {
            fieldChangeCallBack({
              values: newValues as Values,
              type: type as string,
              setValue: setValueWithDirty,
              trigger,
              name,
            });
          }
        });
      } finally {
        chain.pop();
      }
    });
    return () => subscription.unsubscribe();
  }, [
    fieldChangeCallBacks,
    handleSubmit,
    onSubmitHandler,
    throttledSetPersistedValues,
    submitOnChange,
    trigger,
    watch,
    setValueWithDirty,
    evaluateChanges,
  ]);

  useEffect(() => {
    if (state === 'blocked') {
      setAwayModalOpen(true);
    }
  }, [state]);

  useEffect(
    () => () => {
      if (blockerReset) blockerReset();
      clearPendingUnmute();
    },
    [blockerReset, clearPendingUnmute],
  );

  useHandleGraphQLErrors({
    graphQLErrors,
    graphQLErrorsSkipPath,
    graphQLErrorsReplacePathMap,
    setError,
  });

  const errorsByStep = getErrorsByStep(fields, errors);

  return (
    <>
      {isStepsVisible && (
        <Portal portalRootId={pageStepsId}>
          <PageSteps data={steps} errorsByStep={errorsByStep} />
        </Portal>
      )}
      {/* Override via props instead of mutating `methods`: useForm returns the
          same object every render, so a mutated `methods.setValue` would be
          re-read as the wrapper on the next render, re-wrapped, and handed to
          useFormContext consumers with a new identity every render —
          re-firing any effect that depends on it (STAGING-ADMIN-81). */}
      <FormProvider
        {...methods}
        setValue={setValueWithDirty}
        reset={resetForm as typeof methods.reset}
      >
        <FormProviderCustom>
          <Form
            actions={actions}
            actionsComponent={actionsComponent}
            actionsComponentProps={actionsComponentProps}
            autoComplete={autoComplete}
            fieldsArray={fieldsArray}
            formClassName={formClassName}
            formId={formIdProp}
            formItemsClassName={formItemsClassName}
            formState={formState}
            handleSubmit={handleSubmit}
            loading={loading}
            loadingDesc={loadingDesc}
            isDirtyOverride={hasLocalChanges}
            onSubmitHandler={onSubmitHandler}
            renderAfterForm={renderAfterForm}
            sourceFields={sourceFields}
            stepsCount={stepsCount}
          />
        </FormProviderCustom>
      </FormProvider>
      <ConfirmationModal
        confirmButtonLabel={t('leave-without-saving')}
        description={t('changes-are-not-saved-desc')}
        icon={IconName.Warning}
        isOpen={isAwayModalOpen}
        onClose={onCloseAwayModal}
        onConfirm={onAwayConfirmed}
        title={t('changes-are-not-saved')}
      />
    </>
  );
}

export default memo(FormContainer);
